import type { SecondData } from '../../types'

/** Layout constants. Picked to reproduce the previous SVG implementation's
 *  pixel rhythm so the chart slots into the existing dialog with no surprises.
 */
export const ROW_H = 30
export const ROW_TOP_PAD = 3
export const ROW_BOTTOM_PAD = 3
/** Fractions of a row reserved for the data band (curve + fill). The remaining
 *  1 - DATA_BAND_BOTTOM (~22%) is left as deliberate empty space below the
 *  curve so an adjacent row's peak below it can't visually touch this row's
 *  baseline / fill area.
 */
export const DATA_BAND_TOP = 0.12
export const DATA_BAND_BOTTOM = 0.78
export const TIME_AXIS_H = 28
export const LABEL_WIDTH = 140
export const PX_PER_SECOND = 20
export const MIN_INTENSITY = 1
export const MAX_INTENSITY = 5
/** Active-cursor visibility band inside the scroll viewport (px from each
 *  edge) — used by the auto-scroll effect to decide when to recenter. */
export const VIEWPORT_INSET = 16

/** External tooltip layout. */
export const TOOLTIP_WIDTH = 260
export const TOOLTIP_GAP = 42
/** Pixel rhythm for the rows-aligned tooltip layout. */
export const TOOLTIP_PADDING_PX = 12
export const TOOLTIP_FOOTER_HEIGHT = 47

/**
 * Curated 12-colour palette, one entry per motivation row, grouped into six
 * shade pairs (top → bottom). Each consecutive pair shares a hue family with a
 * deeper and a lighter shade so adjacent rows visually cluster instead of
 * reading as a rainbow stripe. Order matches `MOTIVATION_KEYS_IN_ORDER` so
 * the timeline rows, the active-marker dots, and the tooltip swatches all
 * share the same colour key.
 */
export const MOTIVATION_COLORS: readonly string[] = [
  '#4527A0',
  '#9575CD',
  '#00838F',
  '#4DD0E1',
  '#EF6C00',
  '#FFB74D',
  '#1565C0',
  '#64B5F6',
  '#B71C1C',
  '#E57373',
  '#2E7D32',
  '#81C784',
]

/** Look up a motivation's row colour, falling back to a neutral grey if the index is out of range. */
export const colorForRow = (rowIndex: number): string => MOTIVATION_COLORS[rowIndex] ?? '#9E9E9E'

/**
 * Map a 1-5 intensity onto the y-coordinate inside its row. Datasets live on
 * a shared linear y-scale where each integer is a row boundary; intensity 1
 * sits flush against the next row's top edge (= "baseline") and intensity 5
 * peaks near this row's own top edge. Returned y is in scale units, not
 * pixels.
 */
export const intensityToY = (rowIndex: number, intensity: number): number => {
  const clamped = Math.max(MIN_INTENSITY, Math.min(MAX_INTENSITY, intensity))
  const normalized = (clamped - MIN_INTENSITY) / (MAX_INTENSITY - MIN_INTENSITY)
  const top = rowIndex + DATA_BAND_TOP
  const bottom = rowIndex + DATA_BAND_BOTTOM
  return bottom - normalized * (bottom - top)
}

const DOT_RADIUS_MIN = 1.5
const DOT_RADIUS_MAX = 6
/** Power < 1 keeps the low end small while letting the high end punch out a
 *  noticeably larger marker, so the dot silhouette tracks intensity more
 *  visibly than a flat linear ramp. */
const DOT_RADIUS_CURVE = 0.7

/**
 * Map a 1-5 intensity onto an active-marker dot radius (in pixels).
 * Intensity 1 gets a small dot, intensity 5 a larger one so the marker
 * silhouette reads as a quick visual of "how strong" the motivation is at
 * the active second.
 */
export const intensityToDotRadius = (intensity: number): number => {
  const clamped = Math.max(MIN_INTENSITY, Math.min(MAX_INTENSITY, intensity))
  const normalized = (clamped - MIN_INTENSITY) / (MAX_INTENSITY - MIN_INTENSITY)
  const curved = Math.pow(normalized, DOT_RADIUS_CURVE)
  return DOT_RADIUS_MIN + curved * (DOT_RADIUS_MAX - DOT_RADIUS_MIN)
}

/** Round a fractional x value to the nearest available second. */
export const snapToSecond = (rawX: number, seconds: SecondData[]): number | null => {
  if (seconds.length === 0) return null
  let best = seconds[0]
  let bestDist = Math.abs(rawX - best.second)
  for (let i = 1; i < seconds.length; i++) {
    const dist = Math.abs(rawX - seconds[i].second)
    if (dist < bestDist) {
      best = seconds[i]
      bestDist = dist
    }
  }
  return best.second
}
